import React, { useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { merchItems } from '../mock';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const MerchStore = () => {
  const { addToCart } = useCart();
  const { currentTheme } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredMerch = selectedCategory === 'All' 
    ? merchItems 
    : merchItems.filter(item => item.category === selectedCategory);

  const handleAddToCart = (item, size) => {
    if (!size) {
      toast({
        title: "Please select a size",
        description: "Choose a size before adding to cart.",
        variant: "destructive"
      });
      return;
    }
    addToCart({ ...item, type: 'merch', selectedSize: size });
    toast({
      title: "Added to cart",
      description: `${item.name} (${size}) has been added to your cart.`,
    });
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Merch Store</h1>
          <p className="text-xl text-gray-400">Official Tha Advocate Beats merchandise</p>
        </div>

        {/* Category Filter */}
        <div className="mb-8 flex items-center gap-4">
          <span className="text-gray-400">Category:</span>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48 bg-zinc-900 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="All" className="text-white hover:bg-white/5">All Items</SelectItem>
              <SelectItem value="T-Shirt" className="text-white hover:bg-white/5">T-Shirts</SelectItem>
              <SelectItem value="Hoodie" className="text-white hover:bg-white/5">Hoodies</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Merch Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredMerch.map(item => (
            <MerchCard 
              key={item.id} 
              item={item} 
              onAddToCart={handleAddToCart}
              currentTheme={currentTheme}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const MerchCard = ({ item, onAddToCart, currentTheme }) => {
  const [selectedSize, setSelectedSize] = useState('');

  return (
    <div className="group bg-zinc-900 rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
      <div className="relative aspect-square overflow-hidden">
        <img 
          src={item.imageUrl} 
          alt={item.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
        
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-black/70 backdrop-blur-sm text-white">
            {item.category}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-white font-bold text-xl mb-2">{item.name}</h3>
        <p className="text-gray-400 text-sm mb-4">{item.description}</p>
        
        {/* Size Selection */}
        <div className="mb-4">
          <label className="text-gray-400 text-sm mb-2 block">Select Size</label>
          <div className="flex gap-2">
            {item.sizes.map(size => (
              <button
                key={size}
                onClick={() => setSelectedSize(size)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedSize === size
                    ? 'text-black'
                    : 'bg-white/10 text-gray-400 hover:bg-white/20'
                }`}
                style={selectedSize === size ? { backgroundColor: currentTheme.primary } : {}}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold text-white">${item.price}</span>
          <Button
            onClick={() => onAddToCart(item, selectedSize)}
            className="text-black font-bold hover:scale-105 transition-transform"
            style={{ backgroundColor: currentTheme.primary }}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MerchStore;